import { fg_onEmphasis } from "@clockwise/design-system/tokens";
import { Avatar } from "@material-ui/core";
import classNames from "classnames";
import { trim } from "lodash";
import { IconProps, UserCircle } from "phosphor-react";
import React, { useRef, useState } from "react";
import handleBlurParent from "./util/handleBlurParent";

type AvatarMenuItem = {
  Icon: React.FC<IconProps>;
  action: () => void;
  text: string;
};

type Props = {
  user: {
    givenName: string | null;
    familyName: string | null;
    externalImageUrl: string | null;
  } | null;
  menuItems: AvatarMenuItem[];
};

const AvatarMenu = ({ user, menuItems }: Props) => {
  const [open, setOpen] = useState(false);
  const anchorEl = useRef<HTMLButtonElement>(null);

  const handleClick = () => setOpen((state) => !state);

  const handleClose = () => {
    if (open) {
      setOpen(false);
      anchorEl.current?.focus();
    }
  };

  const handleBlur = handleBlurParent(handleClose);

  const fullName = trim(`${user?.givenName ?? ""} ${user?.familyName ?? ""}`) || "";
  const firstInitial = fullName[0] || "";

  return (
    <div className="cw-relative cw-inline-block" onBlur={handleBlur}>
      <button
        aria-label="user action menu"
        className="cw-cursor-pointer cw-border-0 cw-bg-transparent"
        id="avatar_menu-button"
        onClick={handleClick}
        ref={anchorEl}
      >
        <Avatar
          alt={fullName}
          src={user?.externalImageUrl ?? undefined}
          className="cw-bg-brand-emphasis"
        >
          {firstInitial ? (
            firstInitial
          ) : (
            <UserCircle size="40" color={fg_onEmphasis} weight="thin" />
          )}
        </Avatar>
      </button>

      <div
        className={classNames(
          "cw-absolute cw-right-0 cw-z-10 cw-mt-2 cw-min-w-[50px] cw-w-max cw-origin-top-right cw-rounded-sm cw-bg-default cw-shadow-md cw-ring-1 cw-ring-black cw-ring-opacity-5 focus:cw-outline-none",
          {
            "cw-hidden": !open,
          },
        )}
        role="none"
        aria-orientation="vertical"
        tabIndex={-1}
      >
        <div className="py-1" role="none" tabIndex={-1}>
          {menuItems.map(({ Icon, action, text }) => {
            return (
              <button
                key={`avatar_menu-${text}`}
                onClick={() => {
                  action();
                  handleClose();
                }}
                className="cw-flex cw-items-center cw-cursor-pointer cw-w-full cw-border-0 cw-bg-transparent cw-text-muted cw-p-2 cw-text-sm hover:cw-bg-default-hover"
                role="menuitem"
              >
                <Icon color="black" className="cw-pr-1" />
                <span>{text}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AvatarMenu;
